import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { SignaturePad } from '../components/SignaturePad';
import { api, ApiError } from '../lib/api';
import '../styles/templates.css';

const PREVIEW = Boolean(import.meta.env.VITE_PREVIEW);

/** Prepara el cuerpo del contrato para mostrarlo con su formato (tablas, colores). */
function renderSignBody(body: string): string {
  let s = body ?? '';
  // Si llegó escapado (&lt;p&gt;...), revertirlo para poder pintarlo como HTML.
  if (!/</.test(s) && /&lt;/.test(s)) {
    s = s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
  }
  const looksHtml = /<(p|h[1-6]|ul|ol|li|strong|em|u|br|blockquote|table|thead|tbody|tr|td|th|hr|div|span|img|font|mark)[\s>]/i.test(s);
  if (looksHtml) return s;
  // Texto plano: convertir saltos de línea en párrafos para que se lea bien.
  return s.replace(/\r/g, '').split('\n').map((l) => l.trim() ? `<p>${l.replace(/</g, '&lt;')}</p>` : '').join('');
}

interface Status {
  status: string;
  documentNumber: string;
  documentTitle: string | null;
  companyName: string;
  companyLogo: string | null;
  signed: boolean;
  signerName: string | null;
  signedAt: number | null;
  requiresPassword: boolean;
  canDownload: boolean;
}
interface Contract {
  number: string; title: string | null; body: string; amount: string; currency: string;
  date: string | null; counterparty: string;
  company: { name: string; logoUrl: string | null; brandColor: string; footer: string; signatoryName: string; signatoryTitle: string };
}

function triggerDownload(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click();
  a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function printableHtml(c: Contract, signer: string | null, signedAt: number | null): string {
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${c.number}</title>
<style>body{font-family:Georgia,serif;max-width:720px;margin:40px auto;padding:0 24px;color:#1a1a1a;line-height:1.7}
h1{color:${c.company.brandColor}}hr{border:none;border-top:2px solid ${c.company.brandColor};margin:14px 0}
.sig{margin-top:40px;border-top:1px solid #ccc;padding-top:16px}
table{border-collapse:collapse;width:100%;margin:12px 0}td,th{border:1px solid #888;padding:6px 10px}
th{background:#f0f0f0}img{max-width:100%}</style></head>
<body><h1>${c.company.name}</h1><hr><p><strong>Documento ${c.number}</strong></p>
<div class="doc-body">${renderSignBody(c.body)}</div>
<div class="sig"><p><strong>Firmado digitalmente</strong></p><p>Firmante: ${signer ?? '—'}<br>Fecha: ${signedAt ? new Date(signedAt).toLocaleString('es-ES') : '—'}</p></div>
<p style="color:#888;font-size:12px">${c.company.footer}</p></body></html>`;
}

export function SignPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [password, setPassword] = useState('');
  const [contract, setContract] = useState<Contract | null>(null);
  const [signerName, setSignerName] = useState('');
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [phase, setPhase] = useState<'gate' | 'view' | 'signed'>('gate');
  const [signedAt, setSignedAt] = useState<number | null>(null);
  const [signedName, setSignedName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<Status>(`/public/sign/${token}`)
      .then((s) => { setStatus(s); if (s.signed) { setSignedAt(s.signedAt); setSignedName(s.signerName); } })
      .catch(() => setNotFound(true));
  }, [token]);

  const verify = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post<{ signed: boolean; signedAt: number | null; signerName: string | null; contract: Contract }>(`/public/sign/${token}/verify`, { password });
      setContract(r.contract);
      if (r.signed) { setSignedAt(r.signedAt); setSignedName(r.signerName); setPhase('signed'); }
      else setPhase('view');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo verificar.');
    } finally { setBusy(false); }
  };

  const sign = async () => {
    if (signerName.trim().length < 2) { setErr('Escribe tu nombre completo para firmar.'); return; }
    if (!signatureImage) { setErr('Traza tu firma en el recuadro antes de continuar.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api.post<{ signedAt: number; signerName: string }>(`/public/sign/${token}/sign`, { password, signerName: signerName.trim(), signatureImage });
      setSignedAt(r.signedAt); setSignedName(r.signerName); setPhase('signed');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo firmar.');
    } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true); setErr(null);
    try {
      if (PREVIEW && contract) {
        triggerDownload(new Blob([printableHtml(contract, signedName, signedAt)], { type: 'text/html' }), `${contract.number}-firmado.html`);
      } else {
        const res = await fetch(`/api/public/sign/${token}/download`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ password }) });
        if (!res.ok) throw new Error();
        triggerDownload(await res.blob(), `${contract?.number ?? 'contrato'}-firmado.pdf`);
      }
    } catch {
      setErr('No se pudo descargar el documento.');
    } finally { setBusy(false); }
  };

  const accent = contract?.company.brandColor ?? '#205c3e';

  if (notFound) {
    return <div className="sign-page"><div className="sign-sheet"><div className="sign-body" style={{ textAlign: 'center', padding: 60 }}>
      <Icon name="alert" size={32} style={{ color: 'var(--clay)' }} />
      <h2 className="h2" style={{ marginTop: 12 }}>Invalid link</h2>
      <p className="muted">Este enlace de firma no existe o ha caducado.</p>
    </div></div></div>;
  }
  if (!status) return <div className="sign-page"><div className="faint" style={{ marginTop: 80 }}>Loading…</div></div>;

  const expired = status.status === 'expired';
  const revoked = status.status === 'revoked';

  return (
    <div className="sign-page">
      <div className="sign-sheet">
        <div className="sign-brandbar" style={{ background: accent }} />
        <div className="sign-head">
          <div className="sign-head__company">{contract?.company.name ?? status.companyName}</div>
          <div className="sign-head__doc">Documento {status.documentNumber}{status.documentTitle ? ` · ${status.documentTitle}` : ''}</div>
        </div>

        {(expired || revoked) ? (
          <div className="sign-body" style={{ textAlign: 'center', padding: 48 }}>
            <Icon name="alert" size={28} style={{ color: 'var(--clay)' }} />
            <p className="muted" style={{ marginTop: 10 }}>{expired ? 'Este enlace ha caducado.' : 'Este enlace ha sido revocado.'}</p>
          </div>
        ) : phase === 'gate' ? (
          <div className="sign-body">
            <div className="sign-gate">
              <div className="sign-gate__icon" style={{ background: 'var(--accent-tint)', color: accent }}><Icon name="lock" size={24} /></div>
              <h2 className="h2" style={{ marginBottom: 6 }}>{status.signed ? 'Contrato firmado' : 'Contrato pendiente de firma'}</h2>
              <p className="muted" style={{ marginBottom: 18 }}>Enter the password we sent you para {status.signed ? 'acceder y descargar el documento' : 'leer y firmar el contrato'}.</p>
              <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contract password" style={{ textAlign: 'center', letterSpacing: '0.1em' }} onKeyDown={(e) => e.key === 'Enter' && verify()} />
              {err && <div className="act-error" style={{ marginTop: 12 }}><Icon name="alert" size={16} />{err}</div>}
              <button className="btn btn--primary" style={{ width: '100%', marginTop: 14 }} onClick={verify} disabled={busy || !password}>{busy ? 'Comprobando…' : 'Acceder'}</button>
            </div>
          </div>
        ) : phase === 'view' && contract ? (
          <>
            <div className="sign-body">
              <div className="sign-meta">
                <div><div className="sign-meta__k">Cliente</div><div className="sign-meta__v">{contract.counterparty}</div></div>
                <div><div className="sign-meta__k">Fecha</div><div className="sign-meta__v">{contract.date ?? '—'}</div></div>
                <div><div className="sign-meta__k">Importe</div><div className="sign-meta__v">{contract.amount}</div></div>
              </div>
              <div className="sign-contract" dangerouslySetInnerHTML={{ __html: renderSignBody(contract.body) }} />
            </div>
            <div className="sign-actions">
              <div className="field" style={{ marginBottom: 14 }}>
                <label>Nombre y apellidos del firmante</label>
                <input className="input" value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Nombre y apellidos" style={{ fontFamily: 'var(--font-display)', fontSize: 18 }} />
              </div>
              <div className="field" style={{ marginBottom: 12 }}>
                <label>Firma manuscrita</label>
                <SignaturePad onChange={setSignatureImage} />
              </div>
              {err && <div className="act-error" style={{ marginBottom: 12 }}><Icon name="alert" size={16} />{err}</div>}
              <button className="btn btn--primary" style={{ width: '100%' }} onClick={sign} disabled={busy}>{busy ? 'Firmando…' : 'Firmar contrato'}</button>
              <p className="faint" style={{ fontSize: 12, marginTop: 10, textAlign: 'center' }}>Al firmar, tu nombre, tu trazo manuscrito, la fecha y tu IP quedan registrados de forma inmutable y se estampan en el documento.</p>
            </div>
          </>
        ) : (
          <div className="sign-body">
            <div className="sign-signedbox">
              <div className="sign-signedbox__seal" style={{ background: accent }}><Icon name="check" size={30} /></div>
              <h2 className="h2" style={{ marginBottom: 6 }}>Contrato firmado</h2>
              <p className="muted">Firmado por <b>{signedName}</b>{signedAt ? ` el ${new Date(signedAt).toLocaleString('es-ES')}` : ''}.</p>
              {err && <div className="act-error" style={{ marginTop: 14 }}><Icon name="alert" size={16} />{err}</div>}
              <button className="btn btn--accent" style={{ marginTop: 20 }} onClick={download} disabled={busy}>
                <Icon name="download" size={16} />{busy ? 'Preparando…' : 'Descargar documento firmado'}
              </button>
            </div>
          </div>
        )}
      </div>
      <p className="sign-foot">Firma digital segura · {status.companyName}</p>
    </div>
  );
}
